import { useMemo } from 'react'
import {
  APIError,
  type EntityId,
  type PreferenceGetInput,
  type PreferenceGetSuccess,
  type StrictSubareaPreference,
  isDefined,
  unique,
} from '@terros/common'
import { type UseQueryOptions, useQueries, useQuery } from '@tanstack/react-query'
import { preferenceKey } from '../../keys'
import { useServerRequest } from '../../api'

type StrictSubareaState = {
  mode: StrictSubareaPreference
  isLoading: boolean
}

type StrictSubareaPreferenceResults = {
  modes: StrictSubareaPreference[]
  isLoading: boolean
}

const STRICT_SUBAREA_MODE_WEIGHT = new Map<StrictSubareaPreference, number>([
  ['off', 0],
  ['warn', 1],
  ['block', 2],
])

export function useStrictSubareaAssignment(targetIds: EntityId[] = []): StrictSubareaState {
  const { modes, isLoading } = useStrictSubareaPreferenceResults(targetIds)

  const mode: StrictSubareaPreference = useMemo(() => {
    return modes.reduce<StrictSubareaPreference>((strictest, current) => {
      const strictestWeight = STRICT_SUBAREA_MODE_WEIGHT.get(strictest) ?? 0
      const currentWeight = STRICT_SUBAREA_MODE_WEIGHT.get(current) ?? 0
      return currentWeight > strictestWeight ? current : strictest
    }, 'off')
  }, [modes])

  return {
    mode,
    isLoading,
  }
}

function useStrictSubareaPreferenceResults(targetIds: EntityId[]): StrictSubareaPreferenceResults {
  const serverRequest = useServerRequest<
    PreferenceGetInput<'strictSubareaAssignment'>,
    PreferenceGetSuccess<'strictSubareaAssignment'>
  >('/preference/get')
  const uniqueTargetIds = useMemo(() => unique(targetIds), [targetIds])
  const readPreference = (
    targetId?: EntityId
  ): UseQueryOptions<PreferenceGetSuccess<'strictSubareaAssignment'>, Error, StrictSubareaPreference> => ({
    queryKey: preferenceKey('strictSubareaAssignment', targetId),
    queryFn: () => serverRequest({ preference: 'strictSubareaAssignment', targetId }),
    select: (result: PreferenceGetSuccess<'strictSubareaAssignment'>) => result.value,
    retry: false,
  })
  const targetResults = useQueries({ queries: uniqueTargetIds.map(readPreference) })
  const isAnyTargetDenied = targetResults.some((result) => isNotAuthorized(result.error))
  // Reading one's own preference is always allowed, so a denied team falls back to the caller's own
  // effective mode instead of a warning that a visibility-restricted manager could never clear.
  const selfResult = useQuery({ ...readPreference(), enabled: isAnyTargetDenied })

  return useMemo(
    () => ({
      modes: targetResults.map((result) => targetMode(result, selfResult)).filter(isDefined),
      isLoading: targetResults.some((result) => result.isLoading) || (isAnyTargetDenied && selfResult.isPending),
    }),
    [targetResults, selfResult, isAnyTargetDenied]
  )
}

type ModeRead = { data?: StrictSubareaPreference; error: unknown }

/**
 * Undefined while the mode is still loading. An unknown mode never resolves to 'off', which would
 * silently drop the team's check: it warns without blocking.
 */
function targetMode(target: ModeRead, self: ModeRead): StrictSubareaPreference | undefined {
  if (isDefined(target.data)) return target.data
  if (!target.error) return undefined
  if (!isNotAuthorized(target.error)) return 'warn'
  if (isDefined(self.data)) return self.data
  return self.error ? 'warn' : undefined
}

function isNotAuthorized(error: unknown): boolean {
  return error instanceof APIError && error.error.error === 'NotAuthorized'
}
